import React, { useCallback, useEffect, useState } from "react";
import { Box, Button } from "@chakra-ui/react";
import { MergeOptions, UserMergeKey } from "../SyncConfig/ConfigForm/MergeOptions";
import { ErrorOption, FieldPath, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { Text, Image, IconButton, Spinner } from "@chakra-ui/react";
import { TextFieldControl } from "../SyncConfig/ConfigForm/TextFieldControl";
import { CheckBoxControl } from "../SyncConfig/ConfigForm/CheckboxControl";
import { AttributeConfig, YearAttributes, defaultCleverYearAttributes, YearAttributesToInput } from "../sis-integration/AttributeConfig";
import PageTitle from "../../title/PageTitle";
import { Icon, useToast } from "@familyzone/component-library";
import { purgeSyncData } from "../../../utils/api/Purge";
import { SyncStatus } from "./SyncStatus";
import { SyncMetricKeyEnum } from "../../enums/SysncMetricKeyEnum";
import { useSessionStore } from "../../../storez/SessionStore";
import { YearAttributeEnum } from "../sis-integration/SisIntegration";
import { err, ok, Result } from "./ConfigForm/Common";
import { PurgeConfirmModal } from "./ConfigForm/PurgeConfirmModal";
import { executeSync, extractSyncError, getSyncByType, startOAuth, SyncType } from "../../../utils/api/SyncApi";
import { cleverNodeToResponse, saveCleverSync } from "./cleverSyncMapper";

interface ValidationError<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues> | `root.${string}` | "root";
  error: ErrorOption;
}

type ManuallyRanSyncs = Record<string, number>;
export interface XmlHttpResponse {
  responseJSON?: { error?: string };
}

export interface MergeConfigMethodComputed {
  __typename: "ComputedMergeMethod";
  expression: string;
}

export interface MergeConfigMethodEmail {
  __typename: "EmailMergeMethod";
  stripped: boolean;
}

type MergeConfigMethodComputedInput = Omit<MergeConfigMethodComputed, "__typename">;
type MergeConfigMethodEmailInput = Omit<MergeConfigMethodEmail, "__typename">;

export interface SyncAttributeMapping {
  customerValue: string;
  enumValue: YearAttributeEnum;
}

export interface SyncAttributes {
  years: {
    enabled: boolean;
    mappings: Array<SyncAttributeMapping>;
  };
}

export type MergeConfigMethod = MergeConfigMethodComputed | MergeConfigMethodEmail;

export interface MergeConfigMethodInput {
  computed?: MergeConfigMethodComputedInput;
  email?: MergeConfigMethodEmailInput;
}

export interface MergeConfig<Method = MergeConfigMethod> {
  priority: number;
  method: Method;
  attachOnly?: boolean;
}

export type MergeConfigInput = MergeConfig<MergeConfigMethodInput>;

export interface FormState {
  id: string;
  client_id: string;
  district_id: string;
  enabled: boolean;
  match_users_by: MatchBy;

  sync_classroom_data: boolean;
  force_unique_classroom_names: boolean;

  sync_parent_data: boolean;
  sync_parent_type_parent: boolean;
  sync_parent_type_family: boolean;
  sync_parent_type_emergency: boolean;
  sync_parent_type_other: boolean;

  user_merge_priority: number;
  user_merge_key: UserMergeKey;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;

  sync_year_attributes: boolean;
  year_attribute_mappings?: YearAttributes;
}

export interface Config<Merge = MergeConfig> {
  enabled: boolean;
  districtID: string;
  matchUsersByName: boolean;
  matchUsersByUsername: boolean;
  forceUniqueName: boolean;
  syncParents: boolean;
  syncClassroomData: boolean;
  syncParentTypes?: string[];
  merge?: Merge;
  syncAttributes?: SyncAttributes;
}

export type ConfigInput = Config<MergeConfigInput>;

export interface SyncApiConfigInner {
  id: string;
  config: Config;
}

export interface SyncApiConfigResponse {
  configuration?: SyncApiConfigInner | null;
  clever_client_id: string;
}

interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export enum MatchBy {
  EMAIL_ONLY = "EMAIL_ONLY",
  FIRST_AND_LAST_NAME_OR_EMAIL = "FIRST_AND_LAST_NAME_OR_EMAIL",
  USERNAME_OR_EMAIL = "USERNAME_OR_EMAIL",
}

export enum ParentType {
  PARENT_OR_GUARDIAN = "Parent/Guardian",
  FAMILY = "Family",
  EMERGENCY = "Emergency",
  OTHER = "Other",
}

const SECTION_STYLE = {
  display: "flex",
  flexDir: "column",
  gap: "1rem",
};

const NESTED_SECTION_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  paddingLeft: `20px`,
};

const toFormState = (x: SyncApiConfigResponse): FormState => {
  const toMatchBy = (x: SyncApiConfigInner): MatchBy => {
    if (!x.config.matchUsersByName && !x.config.matchUsersByUsername) {
      return MatchBy.EMAIL_ONLY;
    } else if (x.config.matchUsersByName) {
      return MatchBy.FIRST_AND_LAST_NAME_OR_EMAIL;
    } else if (x.config.matchUsersByUsername) {
      return MatchBy.USERNAME_OR_EMAIL;
    }

    // Failsafe.
    return MatchBy.EMAIL_ONLY;
  };

  const toMergeKey = (x: SyncApiConfigInner): UserMergeKey => {
    const merge = x.config?.merge;
    if (!merge?.method) {
      return UserMergeKey.Email;
    }

    if (merge.method.__typename === "ComputedMergeMethod") {
      return UserMergeKey.Computed;
    } else if (merge.method.__typename === "EmailMergeMethod" && merge.method.stripped) {
      return UserMergeKey.EmailStripped;
    } else {
      return UserMergeKey.Email;
    }
  };

  const toYearAttributeMappings = (x: Array<SyncAttributeMapping>): YearAttributes => {
    const attrs: YearAttributes = {};
    for (const e of x) {
      attrs[e.enumValue] = e.customerValue;
    }

    return attrs;
  };

  if (!x.configuration) {
    console.error("undefined config, using defaults");
    // clever_client_id is a server environment variable, not a per-customer config field.
    // It must be preserved when no customer configuration exists in order to facilitate
    // the initial setup of Clever integration.
    return { ...DEFAULT_INPUTS, client_id: x.clever_client_id };
  }

  const config = x.configuration.config;
  const mergeKey = toMergeKey(x.configuration);
  let mergeExpression = "";
  if (mergeKey === UserMergeKey.Computed) {
    mergeExpression = (config.merge?.method as MergeConfigMethodComputed).expression;
  }

  return {
    id: x.configuration.id,
    client_id: x.clever_client_id,
    district_id: config.districtID,
    enabled: config.enabled,
    match_users_by: toMatchBy(x.configuration),

    sync_classroom_data: config.syncClassroomData,
    force_unique_classroom_names: config.forceUniqueName,

    sync_parent_data: config.syncParents,
    sync_parent_type_parent: config?.syncParentTypes?.includes(ParentType.PARENT_OR_GUARDIAN) ?? false,
    sync_parent_type_family: config?.syncParentTypes?.includes(ParentType.FAMILY) ?? false,
    sync_parent_type_emergency: config?.syncParentTypes?.includes(ParentType.EMERGENCY) ?? false,
    sync_parent_type_other: config?.syncParentTypes?.includes(ParentType.OTHER) ?? false,

    user_merge_key: toMergeKey(x.configuration),
    user_merge_priority: config.merge?.priority ?? 10,
    user_merge_expression: mergeExpression,
    user_merge_attach_only: config.merge?.attachOnly || false,

    sync_year_attributes: config.syncAttributes?.years?.enabled ?? true,
    // A sync stored without year mappings reads back with an empty list, so pre-fill the Clever defaults for it
    year_attribute_mappings: config.syncAttributes?.years?.mappings?.length
      ? toYearAttributeMappings(config.syncAttributes.years.mappings)
      : defaultCleverYearAttributes,
  };
};

const toConfigInput = (x: FormState): Result<ConfigInput, ValidationError<FormState>> => {
  const toParentTypes = (x: FormState): ParentType[] => {
    const parentTypes = [];

    if (x.sync_parent_type_emergency) parentTypes.push(ParentType.EMERGENCY);
    if (x.sync_parent_type_family) parentTypes.push(ParentType.FAMILY);
    if (x.sync_parent_type_other) parentTypes.push(ParentType.OTHER);
    if (x.sync_parent_type_parent) parentTypes.push(ParentType.PARENT_OR_GUARDIAN);

    return parentTypes;
  };

  const toMergeConfig = (x: FormState): MergeConfigInput => {
    let method: MergeConfigMethodInput;
    if (x.user_merge_key === UserMergeKey.Computed) {
      method = {
        computed: {
          expression: x.user_merge_expression ?? "",
        },
      };
    } else {
      method = {
        email: {
          stripped: x.user_merge_key === UserMergeKey.EmailStripped,
        },
      };
    }

    return {
      priority: x.user_merge_priority,
      method: method,
      attachOnly: x.user_merge_attach_only,
    };
  };

  const toSyncAttributes = (x: FormState): Result<SyncAttributes, ValidationError<FormState>> => {
    const { mappings, duplicateKeys } = YearAttributesToInput(x.year_attribute_mappings);
    if (duplicateKeys.size > 0) {
      return err({
        name: `sync_year_attributes`,
        error: {
          type: "validate",
          message: "Cannot have duplicate values across inputs.",
        },
      });
    }

    const result: Array<SyncAttributeMapping> = Object.entries(mappings).map(([key, value]) => ({
      customerValue: key,
      enumValue: value,
    }));

    return ok({
      years: {
        enabled: x.sync_year_attributes,
        mappings: result,
      },
    });
  };

  const syncAttributes = toSyncAttributes(x);
  if (syncAttributes._tag === "err") {
    return err(syncAttributes.error);
  }

  return ok({
    enabled: x.enabled,
    districtID: x.district_id,
    matchUsersByName: x.match_users_by === MatchBy.FIRST_AND_LAST_NAME_OR_EMAIL,
    matchUsersByUsername: x.match_users_by === MatchBy.USERNAME_OR_EMAIL,
    forceUniqueName: x.force_unique_classroom_names,
    syncParents: x.sync_parent_data,
    syncClassroomData: x.sync_classroom_data,
    syncParentTypes: toParentTypes(x),
    merge: toMergeConfig(x),
    syncAttributes: syncAttributes.value,
  });
};

const DEFAULT_INPUTS: FormState = {
  id: "",
  client_id: "",
  district_id: "",
  enabled: false,
  match_users_by: MatchBy.EMAIL_ONLY,

  sync_classroom_data: false,
  force_unique_classroom_names: false,

  sync_parent_data: false,
  sync_parent_type_parent: false,
  sync_parent_type_family: false,
  sync_parent_type_emergency: false,
  sync_parent_type_other: false,

  user_merge_priority: 10,
  user_merge_expression: undefined,
  user_merge_key: UserMergeKey.Email,
  user_merge_attach_only: false,

  sync_year_attributes: true,
  year_attribute_mappings: defaultCleverYearAttributes,
};

const Section = ({ title, children, subtitle = "" }: SectionProps): JSX.Element => {
  return (
    <Box sx={SECTION_STYLE}>
      <Box backgroundColor="neutrals.20" p="sp8">
        <Text fontFamily="Poppins" fontSize="xl">
          {title}
        </Text>
        {subtitle !== "" && <Text fontFamily="Poppins">{subtitle}</Text>}
      </Box>

      {children}
    </Box>
  );
};

interface CleverConfigurationProps {
  location?: {
    state?: {
      linkError?: string;
    };
  };
}

export const CleverConfiguration: React.FC<CleverConfigurationProps> = ({ location }) => {
  const { successToast, errorToast } = useToast();

  // Load the form state from SM-API (sync-api v2).
  const loadFormState = useCallback(async (): Promise<FormState> => {
    try {
      const node = await getSyncByType(SyncType.Clever);
      return toFormState(cleverNodeToResponse(node));
    } catch (e) {
      errorToast({
        title: "Failed to fetch Clever configuration",
        description: extractSyncError(e, "Unknown error"),
      });
      return DEFAULT_INPUTS;
    }
  }, [errorToast]);

  const {
    register,
    handleSubmit,
    watch,
    control,
    clearErrors,
    reset,
    setError,
    formState: { errors, isLoading, isDirty, isSubmitting },
  } = useForm<FormState>({
    mode: "onBlur",
    defaultValues: loadFormState,
  });

  const enabled = watch("enabled");
  const syncYearAttributes = watch("sync_year_attributes");
  const syncParentData = watch("sync_parent_data");
  const syncClassroomData = watch("sync_classroom_data");
  const mergeKey = watch("user_merge_key");
  const syncParentTypeParent = watch("sync_parent_type_parent");
  const syncParentTypeFamily = watch("sync_parent_type_family");
  const syncParentTypeEmergency = watch("sync_parent_type_emergency");
  const syncParentTypeOther = watch("sync_parent_type_other");
  const districtId = watch("district_id");
  const id = watch("id");

  const [device] = useSessionStore((state) => [state.device?.id ?? ""] as const);
  const [showPurgeModal, setShowPurgeModal] = useState<boolean>(false);
  const [isPurging, setIsPurging] = useState<boolean>(false);

  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const forceReload = useCallback(() => {
    void (async () => {
      reset(await loadFormState());
    })();
  }, [loadFormState, reset]);

  const onSubmit: SubmitHandler<FormState> = (data) => {
    const output = toConfigInput(data);
    if (output._tag === "err") {
      setError(output.error.name, output.error.error);
      return;
    }

    // SM-API: create-or-update the singleton CLEVER sync.
    saveCleverSync(output.value, data.id || undefined)
      .then(() => {
        successToast({ title: "Saved Clever configuration" });
        forceReload();
      })
      .catch((e) => {
        errorToast({
          title: "Failed to save Clever configuration",
          description: extractSyncError(e, "An unknown error occurred while trying to save configuration."),
        });
      });
  };

  const onLogin = useCallback(() => {
    // SM-API OAuth: start returns the Clever authorization URL + single-use state
    // to persist; sync-api holds the client_id and captures the district on complete.
    void (async () => {
      if (!id) {
        errorToast({
          title: "Save the configuration first",
          description: "Please save the sync configuration before logging in with Clever.",
        });
        return;
      }
      try {
        const redirectURI = `${window.location.origin}/config/login/admin/clever/callback`;
        const auth = await startOAuth(id, redirectURI);
        localStorage.setItem("oauth_state_CLEVER", auth.state);
        window.location.href = auth.authorizationURL;
      } catch (e) {
        errorToast({
          title: "Failed to link Clever credentials",
          description: extractSyncError(e, "Could not start credential linking. Please try again."),
        });
      }
    })();
  }, [id, errorToast]);

  const onSync = useCallback(() => {
    const markLockout = () => {
      const manuallyRanSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") ?? "{}") as ManuallyRanSyncs;
      const now = new Date();
      manuallyRanSyncs[device.concat("_CleverLockout")] = now.getTime() + 60000; // 60 second 'ttl' to lock out this sync in this browser
      localStorage.setItem("manuallySetRunningSyncs", JSON.stringify(manuallyRanSyncs));
    };

    // SM-API: POST /api/syncs/{id}/execute
    void (async () => {
      if (id) {
        await executeSync(id).catch(() =>
          errorToast({ title: "Failed to start sync", description: "The sync could not be started. Please try again." })
        );
      }
      forceReload();
      markLockout();
    })();
  }, [device, forceReload, id, errorToast]);

  const handlePurge = useCallback(async () => {
    setIsPurging(true);
    try {
      await purgeSyncData(id, device);
      successToast({
        title: "Purge triggered successfully",
      });
      setShowPurgeModal(false);
      forceReload();
    } catch (error) {
      errorToast({
        title: "Failed to trigger purge",
        description: error instanceof Error ? error.message : "An unknown error occurred while trying to purge data.",
      });
    } finally {
      setIsPurging(false);
    }
  }, [id, device, successToast, errorToast, forceReload]);

  const syncLockoutTime = useCallback(() => {
    const manuallyRanSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") ?? "{}") as ManuallyRanSyncs;
    const key = device.concat("_CleverLockout");

    if (key in manuallyRanSyncs) {
      return new Date(manuallyRanSyncs[key]);
    } else {
      return undefined;
    }
  }, [device]);

  const linkError = location?.state?.linkError;
  useEffect(() => {
    if (linkError) {
      errorToast({
        title: "Failed to link Clever credentials",
        description: linkError,
      });
    }
  }, [linkError, errorToast]);

  return (
    <>
      <PageTitle title="Clever" />
      <Box display="flex" padding="24px" gap="16px">
        <Box width="100%">
          {/* Header */}
          <Box background="white" display="flex" flexDir="column" gap="1rem" padding="1rem" borderRadius="6px">
            <Box
              borderBottom="1px solid #e2e2e2"
              padding="20px"
              display="flex"
              flexDir="row"
              alignItems="center"
              justifyContent="space-between"
              gap="1rem"
            >
              <Text data-testid="title" fontSize="24px">
                Clever
              </Text>
              {isSupportAdmin && id && (
                <Button variant="danger" onClick={() => setShowPurgeModal(true)} data-testid="purge-btn">
                  Purge Data
                </Button>
              )}
            </Box>
            {/* Loading spinner */}
            {isLoading && (
              <Box display="flex" justifyContent="center">
                <Spinner size="lg" />
              </Box>
            )}

            {!isLoading && (
              <Box display="flex" flexDir="row" width="100%">
                {/* Config editor */}
                <Box width="100%">
                  {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px" }}>
                      <Section title="General">
                        <CheckBoxControl
                          fieldProps={{
                            name: "enabled",
                            label: "Enabled",
                            tooltip: "When ticked, this Clever sync will run.",
                            control: control,
                            errors: errors,
                          }}
                        />
                      </Section>

                      {enabled && (
                        <>
                          <Section title="Authentication">
                            <Box display="flex" gap="20px" alignItems="center">
                              <TextFieldControl
                                styleType={"column"}
                                fieldProps={{
                                  name: "district_id",
                                  label: "District ID",
                                  tooltip: 'To update district ID, click "Log in with Clever".',
                                  control: control,
                                  errors: errors,
                                  disabled: true,
                                }}
                                inputProps={{ autoComplete: "off" }}
                              />

                              <Icon icon="fa-arrow-left-to-bracket" boxSize="35px" alignSelf="flex-end" marginBottom="4px" />

                              <IconButton
                                aria-label="Log in with Clever"
                                alignSelf="flex-end"
                                height="43px"
                                icon={<Image alt="login" borderRadius="3px" src="/static/images/clever/clever-login.png" />}
                                onClick={() => onLogin()}
                              ></IconButton>
                            </Box>
                          </Section>

                          <Section title="Parents">
                            <CheckBoxControl
                              fieldProps={{
                                name: "sync_parent_data",
                                label: "Sync Parent Data",
                                tooltip: "When ticked, parent data will be synced.",
                                control: control,
                                errors: errors,
                                rules: {
                                  validate: (_: boolean) => {
                                    if (
                                      syncParentData &&
                                      !syncParentTypeParent &&
                                      !syncParentTypeFamily &&
                                      !syncParentTypeEmergency &&
                                      !syncParentTypeOther
                                    ) {
                                      return "At least one relationship type must be synced.";
                                    } else if (!syncParentData && !syncClassroomData) {
                                      return "At least one of parent data or classroom data must be synced.";
                                    }
                                    return true;
                                  },
                                },
                              }}
                            />

                            {syncParentData && (
                              <Box sx={NESTED_SECTION_STYLE}>
                                <Section title="Sync Relationships" subtitle="">
                                  <CheckBoxControl
                                    fieldProps={{
                                      name: "sync_parent_type_parent",
                                      label: "Parents and Guardians",
                                      tooltip: "When ticked, student relationships of type 'parents and guardians' will be synced.",
                                      control: control,
                                      errors: errors,
                                      rules: {
                                        deps: "sync_parent_data",
                                      },
                                    }}
                                  />

                                  <CheckBoxControl
                                    fieldProps={{
                                      name: "sync_parent_type_family",
                                      label: "Family",
                                      tooltip: "When ticked, student relationships of type 'family' will be synced.",
                                      control: control,
                                      errors: errors,
                                      rules: {
                                        deps: "sync_parent_data",
                                      },
                                    }}
                                  />

                                  <CheckBoxControl
                                    fieldProps={{
                                      name: "sync_parent_type_emergency",
                                      label: "Emergency",
                                      tooltip: "When ticked, student relationships of type 'emergency' will be synced.",
                                      control: control,
                                      errors: errors,
                                      rules: {
                                        deps: "sync_parent_data",
                                      },
                                    }}
                                  />

                                  <CheckBoxControl
                                    fieldProps={{
                                      name: "sync_parent_type_other",
                                      label: "Other",
                                      tooltip: "When ticked, student relationships of type 'other' will be synced.",
                                      control: control,
                                      errors: errors,
                                      rules: {
                                        deps: "sync_parent_data",
                                      },
                                    }}
                                  />
                                </Section>
                              </Box>
                            )}
                          </Section>

                          <Section title="Classrooms">
                            <CheckBoxControl
                              fieldProps={{
                                name: "sync_classroom_data",
                                label: "Sync Classroom Data",
                                tooltip: "When ticked, classroom data will be synced.",
                                control: control,
                                errors: errors,
                              }}
                            />

                            {syncClassroomData && (
                              <Box sx={NESTED_SECTION_STYLE}>
                                <CheckBoxControl
                                  fieldProps={{
                                    name: "force_unique_classroom_names",
                                    label: "Force Unique Classroom Names",
                                    tooltip: "When ticked, uniqueness is enforced for classroom names.",
                                    control: control,
                                    errors: errors,
                                  }}
                                />
                              </Box>
                            )}
                          </Section>

                          <Section title="Attributes">
                            <AttributeConfig
                              clearErrors={clearErrors}
                              syncYearAttributes={syncYearAttributes}
                              errors={errors}
                              control={control}
                            />
                          </Section>

                          <Section title="Merge">
                            <MergeOptions errors={errors} register={register} control={control} mergeKey={mergeKey} />
                          </Section>
                        </>
                      )}
                    </Box>

                    <Button
                      data-testid={"submitBtn"}
                      mt={4}
                      disabled={!isDirty || isSubmitting}
                      colorScheme="teal"
                      type="submit"
                      variant={"primary"}
                    >
                      Submit
                    </Button>
                  </form>
                </Box>

                {/* Sync panel */}
                {enabled && (
                  <Box width="500px" gap="16px" display="flex" flexDir="column">
                    <SyncStatus
                      handleRunSync={districtId && !isDirty ? onSync : undefined}
                      syncLockoutTime={syncLockoutTime()}
                      syncType="CLEVER"
                      syncId={id || undefined}
                      metricsToHide={[SyncMetricKeyEnum.userClassroomsDeleted, SyncMetricKeyEnum.userGuardiansDeleted]}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <PurgeConfirmModal
        isOpen={showPurgeModal}
        onClose={() => setShowPurgeModal(false)}
        onConfirm={() => void handlePurge()}
        isLoading={isPurging}
      />
    </>
  );
};

export default CleverConfiguration;
